import { Box, Dialog, IconButton } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import type { FC } from 'react';
import { useCallback } from 'react';
import type { CreativeEntry } from '../types';

import {
  CreativeAccordion,
  CreativeAccordionDetails,
  CreativeAccordionSummary,
  CreativeAppName,
  CreativeMetric,
  CreativeNavPanel,
  CreativePaper,
  CreativeDetails,
  CreativeDateSeen,
  CreativeAppIosLink,
  CreativePreview,
} from './CreativeComponents';
import { Close, ExpandMoreOutlined, ZoomOutMapOutlined } from '@mui/icons-material';
import useCreativeChannelIcon from '../hooks/useCreativeChannelIcon';
import { useModal } from '../../../hooks';
import CreativeTagsForm from './CreativeTagsForm';
import { CreativeExpansionContext } from '../contexts/CreativeExpansionContext';

interface CreativeItemProps {
  creativeEntry: CreativeEntry;
}

const CreativeItem: FC<CreativeItemProps> = ({ creativeEntry }) => {
  const { expandedAll } = useContext(CreativeExpansionContext);
  const { isOpen: isOpenDialog, open: handleOpenDialog, close: handleCloseDialog } = useModal();
  const [creative] = creativeEntry.creative_items;
  const { ChannelIcon } = useCreativeChannelIcon(creative.channel);
  const [expanded, setExpanded] = useState(false);
  const [defaultExpandedCommentTags, setDefaultExpandedCommentTags] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setExpanded(expandedAll);
    });
  }, [expandedAll]);

  const handleExpansion = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, [setExpanded]);

  const handleExpansionCommentTags = useCallback(() => {
    handleOpenDialog();
    setDefaultExpandedCommentTags(true);
  }, [setDefaultExpandedCommentTags, handleOpenDialog]);

  return (
    <>
      <CreativePaper id={`creative-${creative.id}-paper`}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'start',
            p: 1,
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 0.75,
            }}
          >
            <ChannelIcon />
            <CreativeAppName>{creative.app_name ?? creative.domain}</CreativeAppName>
          </Box>
          <CreativeAccordion expanded={expanded} onChange={handleExpansion}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                height: 24,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'start',
                  justifyContent: 'start',
                  flexWrap: 'wrap',
                  columnGap: 0.75,
                  rowGap: 0.1,
                }}
              >
                <CreativeMetric type={'likes'} value={`${creative.impressions}K`} />
                <CreativeMetric type={'interval'} value={creative.last_seen} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'start',
                  justifyContent: 'start',
                  gap: 1 / 2,
                }}
              >
                <IconButton size={'small'} sx={{ p: 0 }} onClick={handleOpenDialog}>
                  <ZoomOutMapOutlined fontSize={'small'} />
                </IconButton>
                <CreativeAccordionSummary>
                  <IconButton size={'small'} sx={{ p: 0 }}>
                    <ExpandMoreOutlined
                      sx={{ transition: 'transform 0.2s', transform: `rotate(${expanded ? 180 : 0}deg)` }}
                    />
                  </IconButton>
                </CreativeAccordionSummary>
              </Box>
            </Box>
            <CreativeAccordionDetails>
              <CreativeDetails title={creative.title} textHtml={creative.message} />
              <CreativeDateSeen firstSeen={creative.first_seen} lastSeen={creative.last_seen} />
              <CreativeAppIosLink id={creative.app_ios_store_id} />
            </CreativeAccordionDetails>
          </CreativeAccordion>
        </Box>
        <Box sx={{ position: 'relative', display: 'flex', flexGrow: 1 }}>
          <CreativePreview creative={creative} />
          <CreativeNavPanel creative={creative} handleExpansionCommentTags={handleExpansionCommentTags} />
        </Box>
      </CreativePaper>

      <Dialog
        PaperProps={{ sx: { maxWidth: 860, height: 'auto', width: 'auto', p: 0, '&': { overflowY: 'visible' } } }}
        transitionDuration={{
          appear: 0,
          enter: 0,
          exit: 0,
        }}
        open={isOpenDialog}
        onClose={handleCloseDialog}
      >
        <Box
          sx={() => ({
            display: 'flex',
            width: '100%',
            height: '100%',
            justifyContent: 'space-between',
            overflow: 'hidden',
            borderRadius: '8px',
          })}
        >
          <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <CreativePaper id={`creative-${creative.id}-dialog`}>
              <CreativePreview creative={creative} />
              <CreativeNavPanel creative={creative} />
            </CreativePaper>
          </Box>
          <Box sx={{ flex: 1, p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                height: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'start',
                    gap: 0.75,
                    mb: 1,
                  }}
                >
                  <ChannelIcon />
                  <CreativeAppName>{creative.app_name ?? creative.domain}</CreativeAppName>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'start',
                    justifyContent: 'start',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CreativeMetric type={'likes'} value={`${creative.impressions}K`} />
                  <CreativeMetric type={'interval'} value={creative.last_seen} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <CreativeDetails title={creative.title} textHtml={creative.message} />
                  <CreativeTagsForm
                    tags={creativeEntry.tags}
                    updatedAt={creativeEntry.updated_at}
                    creativeId={creativeEntry.creative_id}
                    comment={creativeEntry.comment}
                    isExpanded={defaultExpandedCommentTags}
                  />
                </Box>
              </Box>
              <Box sx={{ pt: 1 }}>
                <CreativeDateSeen firstSeen={creative.first_seen} lastSeen={creative.last_seen} />
                <CreativeAppIosLink id={creative.app_ios_store_id} />
              </Box>
            </Box>
          </Box>
        </Box>
        <IconButton onClick={handleCloseDialog} sx={{ position: 'absolute', top: -6, right: -68 }}>
          <Close sx={{ fontSize: 32, color: 'white' }} />
        </IconButton>
      </Dialog>
    </>
  );
};

export default CreativeItem;
